import { useState } from "react";
import Image from "next/image";
import styles from "./MediaSlideshow.module.css";
import { Icon, VisuallyHidden } from "@zapier/design-system";
import { Carousel } from "@zapier/design-system-beta";
import { crossDomainImageLoader } from "src/utils/images";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";

export type MediaItem = {
  type: "image" | "video";
  src: string;
  alt?: string;
};

function VideoItem({
  src,
  isThumbnail,
}: {
  src: string;
  isThumbnail: boolean;
}) {
  return isThumbnail ? (
    <div className={styles.videoThumbnail}>
      <video
        src={`${src}#t=1`}
        disablePictureInPicture
        disableRemotePlayback
        muted
      />
      <div className={styles.playIcon}>
        <Icon name="actionPlayFill" size={20} color="PrimeWhite" />
      </div>
    </div>
  ) : (
    <video
      src={src}
      controls
      autoPlay
      disablePictureInPicture
      disableRemotePlayback
      controlsList="nodownload"
    >
      <track default kind="captions" srcLang="en" src="" />
    </video>
  );
}

export default function MediaSlideshow({
  mediaItems,
}: {
  mediaItems: MediaItem[];
}) {
  const [currentItem, setCurrentItem] = useState<number>(0);

  const handleCarouselArrowClick = (direction: "left" | "right") => {
    emitUserInteractionEvent(
      UserInteractionEventName.mediaSlideshowCarouselArrowClicked,
      {
        event_action: "click",
        option_selected: direction,
      },
    );
  };

  const handleThumbnailSelected = (index: number) => {
    emitUserInteractionEvent(
      UserInteractionEventName.mediaSlideshowItemSelected,
      {
        event_action: "click",
        option_selected: index.toString(),
      },
    );
  };

  return mediaItems && mediaItems.length > 0 ? (
    <div>
      <div
        className={styles.currentItem}
        id="current-media-item"
        role="tabpanel"
        aria-labelledby={`media-item-${currentItem}`}
      >
        {mediaItems[currentItem].type === "image" ? (
          <Image
            loader={crossDomainImageLoader}
            src={mediaItems[currentItem].src}
            alt={mediaItems[currentItem].alt || ""}
            fill
            priority
          />
        ) : (
          <VideoItem src={mediaItems[currentItem].src} isThumbnail={false} />
        )}
      </div>
      {mediaItems.length > 1 && (
        <Carousel
          elementWidth={120}
          gap={16}
          paddingBottom={1}
          className={styles.carousel}
          role="tablist"
          aria-label="Media slides"
          onLeftArrowClick={() => handleCarouselArrowClick("left")}
          onRightArrowClick={() => handleCarouselArrowClick("right")}
        >
          {mediaItems.map((item, index) => (
            <button
              role="tab"
              id={`media-item-${index}`}
              aria-controls="current-media-item"
              key={index}
              className={styles.mediaItemThumbnail}
              onFocus={() => {
                setCurrentItem(index);
                handleThumbnailSelected(index);
              }}
              aria-selected={currentItem === index}
            >
              <VisuallyHidden>Thumbnail of {item.type} </VisuallyHidden>
              {item.type === "image" ? (
                <Image
                  loader={crossDomainImageLoader}
                  src={item.src}
                  alt={item.alt || ""}
                  fill
                />
              ) : (
                <>
                  <VideoItem src={item.src} isThumbnail={true} />
                  {item.alt && <VisuallyHidden>{item.alt}</VisuallyHidden>}
                </>
              )}
            </button>
          ))}
        </Carousel>
      )}
    </div>
  ) : null;
}
